import React from 'react';
import { Box, Typography, Button, Paper, Container, Alert, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Warning as WarningIcon, CheckCircle as CheckIcon, ArrowForward as ArrowIcon } from '@mui/icons-material';

const WhyStillRefreshing: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom color="error">
          🔄 為什麼還是會一直刷新？
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            🎯 問題原因：您仍在使用傳統路由模式
          </Typography>
          <Typography variant="body2">
            從您的日誌可以看出，您訪問的是 <code>/v/Cm-0R1fsg48</code>、<code>/manage</code> 等傳統路由，
            這些路由仍然會導致頁面刷新。新的 SPA 功能只在 <code>/app</code> 路由中生效。
          </Typography>
        </Alert>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            📊 路由模式對比：
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <WarningIcon color="error" />
              </ListItemIcon>
              <ListItemText 
                primary="傳統路由模式 (會刷新)" 
                secondary={
                  <Box>
                    <Typography variant="body2" color="error">
                      ❌ /notes - 筆記頁面
                    </Typography>
                    <Typography variant="body2" color="error">
                      ❌ /manage - 管理頁面  
                    </Typography>
                    <Typography variant="body2" color="error">
                      ❌ /v/xxx - 影片播放頁面
                    </Typography>
                    <Typography variant="body2" color="error">
                      ❌ /main - 主頁面
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="SPA 模式 (無刷新)" 
                secondary={
                  <Box>
                    <Typography variant="body2" color="success.main">
                      ✅ /app - 單頁應用模式
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      ✅ 所有頁面狀態保持
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      ✅ 瞬間切換，無等待時間
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      ✅ 智能返回導航
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          </List>
        </Box>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            🔍 從您的日誌分析：
          </Typography>
          <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
{`pathname: '/v/Cm-0R1fsg48'  ← 傳統 MainPage 路由
pathname: '/manage'           ← 傳統 ManagePages 路由

沒有找到以下 SPA 相關日誌：
- isEmbedded: true
- MainLayout
- /app 路由`}
            </Typography>
          </Paper>
        </Box>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            🚀 解決方案：
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <ArrowIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="1. 訪問 SPA 模式" 
                secondary="點擊下方按鈕進入 /app 路由"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <ArrowIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="2. 使用底部導航" 
                secondary="在 SPA 模式中使用底部的三個按鈕切換頁面"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <ArrowIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="3. 測試狀態保持" 
                secondary="切換頁面後再回來，觀察狀態是否保持"
              />
            </ListItem>
          </List>
        </Box>
        
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            size="large"
            onClick={() => navigate('/app')}
            sx={{ px: 4, fontSize: '1.1rem', py: 2 }}
          >
            🚀 進入 SPA 模式 (/app)
          </Button>
          
          <Button 
            variant="outlined" 
            size="large"
            onClick={() => navigate('/spa-test')}
            sx={{ px: 4, fontSize: '1.1rem', py: 2 }}
          >
            📋 查看 SPA 說明
          </Button>
        </Box>
        
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            💡 <strong>提示：</strong>
            現在首頁 <code>/</code> 會自動重定向到 SPA 測試頁面，讓您更容易發現新功能。
            請記住使用 <code>/app</code> 路由來體驗無刷新的頁面切換！
          </Typography>
        </Alert>
      </Paper>
    </Container>
  );
};

export default WhyStillRefreshing;
